import * as React from 'react';
import { ITabItemProps } from './TabItem';
import { TabLabel } from './TabLabel';
import { TabPanel } from './TabPanel';
import { createLocators } from '@u1/locators';
import useStyles from './Tabs.styles';

export const { locators } = createLocators({
  name: 'Tabs',
  componentLocators: ['container', 'tabLabels', 'tabPanels'],
})();

export interface ITabsProps {
  children:
    | React.ReactElement<ITabItemProps>[]
    | React.ReactElement<ITabItemProps>;
}

const Tabs: React.FC<ITabsProps> = ({ children }) => {
  const classes = useStyles();

  const [activeTab, setActiveTab] = React.useState('');

  const tabs = React.useMemo(() => {
    let activeTabKey = '';
    const tabsProps = React.Children.toArray(children).map((element) => {
      if (!React.isValidElement(element)) {
        throw new Error(
          'You should pass only valid react elements as children',
        );
      }
      const { active, disabled, label } = element.props;
      const tabKey = (element.key ?? label).toString();
      const isActive: boolean = activeTab
        ? tabKey === activeTab
        : !disabled && (active || !activeTabKey);
      activeTabKey = isActive ? tabKey : activeTabKey;
      return {
        ...element.props,
        tabKey,
        active: isActive,
      } as ITabItemProps & { tabKey: string };
    });

    if (activeTabKey !== activeTab) setActiveTab(activeTabKey);

    return tabsProps;
  }, [activeTab, children]);

  return (
    <div className={classes.root} {...locators.container()}>
      <div
        role="tablist"
        aria-orientation="horizontal"
        className={classes.tabLabels}
        {...locators.tabLabels()}
      >
        {tabs?.map(({ active, disabled, label, tabKey }) => (
          <TabLabel
            key={`tablabel-${tabKey}`}
            disabled={disabled}
            tabKey={tabKey}
            active={active}
            onSelect={setActiveTab}
          >
            {label}
          </TabLabel>
        ))}
      </div>
      <div className={classes.tabPanels} {...locators.tabPanels()}>
        {tabs.map(({ active, tabKey, children: tabPanelChildren }) => (
          <TabPanel key={`tabpanel-${tabKey}`} active={active} tabKey={tabKey}>
            {tabPanelChildren}
          </TabPanel>
        ))}
      </div>
    </div>
  );
};

export default Tabs;
